// One place to answer "what is this simulator showing, relative to this
// campaign?" — the list, campaign detail and Go live drawer all need to
// agree, so none of them derive it independently.
//
// Source: the Ad Spaces Workflows doc, "Rules and states to confirm".
// G1 (live state) and the facility-level part of G2 (licensing) are resolved:
// GET /api/facilities/user/{userId} (v1) already returns Clubhouse_Simulator
// with status.currentAdKit and capabilities.supportsClubhouseAdKit — verified
// against the live swagger (v1 and v2) on 2026-11-03. The exact meaning of
// syncedSimulatorIds vs currentlySyncedSimulatorIds is still an open question
// in that doc; this implements its documented working assumption.

export type SimulatorCampaignRelationship =
  | "LIVE_UP_TO_DATE"
  | "LIVE_OLDER_VERSION"
  | "SENT_NOT_LIVE"
  | "ANOTHER_CAMPAIGN_LIVE"
  | "SET_UP_ON_SIMULATOR"
  | "NOT_SENT";

export type SimulatorCampaignAction =
  | "TAKE_DOWN"
  | "PUSH_UPDATE"
  | "GO_LIVE_HERE"
  | "GO_LIVE_HERE_REPLACES";

export interface SimulatorForCampaignState {
  simulatorId: string;
  currentAdKit?: {
    id: string | null;
    name?: string | null;
    whenActivated?: Date | string;
    /** false = ads set up directly on the sim, not a Clubhouse campaign. */
    isClubhouseAdKit?: boolean;
  } | null;
  isOnline: boolean;
  /** capabilities.supportsClubhouseAdKit — false means the sim's software is too old. */
  isSoftwareSupported: boolean;
}

export interface CampaignForSimulatorState {
  id: string;
  syncedSimulatorIds?: string[];
  currentlySyncedSimulatorIds?: string[];
  /** An archived campaign offers no actions — it's hidden from simulators until restored. */
  deleted?: boolean;
}

export interface SimulatorCampaignState {
  relationship: SimulatorCampaignRelationship;
  isOnline: boolean;
  isSoftwareSupported: boolean;
  /** Empty when the sim is offline or its software isn't supported — there's nothing to offer. */
  actions: SimulatorCampaignAction[];
  /** Set only for ANOTHER_CAMPAIGN_LIVE. */
  otherCampaignName?: string;
  /** Set for the three relationships where some campaign is actually live on the sim. */
  liveSince?: Date | string;
}

const ACTIONS_BY_RELATIONSHIP: Record<
  SimulatorCampaignRelationship,
  SimulatorCampaignAction[]
> = {
  LIVE_UP_TO_DATE: ["TAKE_DOWN"],
  LIVE_OLDER_VERSION: ["PUSH_UPDATE", "TAKE_DOWN"],
  SENT_NOT_LIVE: ["GO_LIVE_HERE"],
  ANOTHER_CAMPAIGN_LIVE: ["GO_LIVE_HERE_REPLACES"],
  SET_UP_ON_SIMULATOR: ["GO_LIVE_HERE_REPLACES"],
  NOT_SENT: ["GO_LIVE_HERE"],
};

const LIVE_RELATIONSHIPS: SimulatorCampaignRelationship[] = [
  "LIVE_UP_TO_DATE",
  "LIVE_OLDER_VERSION",
  "ANOTHER_CAMPAIGN_LIVE",
];

/** Minimal shape of the real SimulatorDto (Models) this module needs. */
export interface SimulatorLike {
  id: string;
  status?: {
    currentAdKit?: {
      id: string | null;
      name?: string | null;
      whenActivated?: Date | string;
      isClubhouseAdKit?: boolean;
    } | null;
    agConnect?: { connected?: boolean };
  };
  capabilities?: { supportsClubhouseAdKit?: boolean };
}

/** Adapts a real SimulatorDto (from getSimulatorsByCurrentFacility) to this module's input. */
export function toSimulatorForCampaignState(
  simulator: SimulatorLike,
): SimulatorForCampaignState {
  return {
    simulatorId: simulator.id,
    currentAdKit: simulator.status?.currentAdKit ?? null,
    isOnline: simulator.status?.agConnect?.connected ?? false,
    // Absence more likely means the field hasn't loaded yet than that the
    // sim is actually unsupported, so only an explicit false blocks actions.
    isSoftwareSupported:
      simulator.capabilities?.supportsClubhouseAdKit !== false,
  };
}

export function deriveSimulatorCampaignState(
  simulator: SimulatorForCampaignState,
  campaign: CampaignForSimulatorState,
): SimulatorCampaignState {
  const currentAdKit = simulator.currentAdKit ?? null;
  const isThisCampaignLive = currentAdKit?.id === campaign.id;
  const isSent = (campaign.syncedSimulatorIds ?? []).includes(
    simulator.simulatorId,
  );
  const isCurrentlySynced = (
    campaign.currentlySyncedSimulatorIds ?? []
  ).includes(simulator.simulatorId);

  let relationship: SimulatorCampaignRelationship;
  if (currentAdKit?.id && !isThisCampaignLive) {
    relationship =
      currentAdKit.isClubhouseAdKit === false
        ? "SET_UP_ON_SIMULATOR"
        : "ANOTHER_CAMPAIGN_LIVE";
  } else if (isThisCampaignLive && isCurrentlySynced) {
    relationship = "LIVE_UP_TO_DATE";
  } else if (isThisCampaignLive) {
    relationship = "LIVE_OLDER_VERSION";
  } else if (isSent || isCurrentlySynced) {
    relationship = "SENT_NOT_LIVE";
  } else {
    relationship = "NOT_SENT";
  }

  const canAct =
    !campaign.deleted && simulator.isOnline && simulator.isSoftwareSupported;

  return {
    relationship,
    isOnline: simulator.isOnline,
    isSoftwareSupported: simulator.isSoftwareSupported,
    actions: canAct ? ACTIONS_BY_RELATIONSHIP[relationship] : [],
    otherCampaignName:
      relationship === "ANOTHER_CAMPAIGN_LIVE"
        ? (currentAdKit?.name ?? undefined)
        : undefined,
    liveSince: LIVE_RELATIONSHIPS.includes(relationship)
      ? currentAdKit?.whenActivated
      : undefined,
  };
}
